import { useState } from 'react';

import { InstallInstructions } from './InstallInstructions.tsx';
import { promptToInstall } from './installStore.ts';
import { useInstallCapability } from './useInstallOffer.ts';

/**
 * The way back to installing, for anyone the banner did not catch.
 *
 * The banner is a single chance and there are several ordinary ways to miss it: turning it
 * down and changing your mind, following a link out of a mail app into a browser that
 * cannot install, or being handed a phone somebody else already said no on. Every one of
 * those ends with a person who wants the icon and has nothing on screen to tap.
 *
 * It reads the *capability* rather than the offer, so a dismissal does not silence it —
 * this is not the app asking, it is the app answering. And it renders nothing at all when
 * the app is already running from the home screen, which is the state most people who come
 * looking for this are actually in.
 *
 * Profile rather than the menu: the menu is six rows and full, and this is a once-per-phone
 * act that nobody hunts for twice.
 */
export function InstallSection() {
  const capability = useInstallCapability();
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  if (!capability) {
    return null;
  }

  return (
    <section className="settings-section">
      <h3 className="settings-section__title">Home screen</h3>

      <div className="settings-row">
        <button
          type="button"
          className="button button--primary button--small"
          onClick={() => {
            if (capability === 'prompt') {
              void promptToInstall();
            } else {
              setIsHelpOpen(true);
            }
          }}
        >
          Add to home screen
        </button>
      </div>

      <p className="settings-hint">
        Opens full screen, straight into the list, and keeps working where the signal does not.
      </p>

      {isHelpOpen && (
        // No dismissal here, unlike the banner: nothing was being offered, so there is
        // nothing to stop offering. Closing the steps just closes them.
        <InstallInstructions onClose={() => setIsHelpOpen(false)} />
      )}
    </section>
  );
}
